'use client'

import { useState, useMemo, useCallback, useEffect, FC } from 'react'
import {
  ReadonlyURLSearchParams,
  usePathname,
  useRouter,
} from 'next/navigation'

import { User, useSetUserCurrentOffice } from '@dc-shared-web/hooks/api/user'
import { BasicTeam, useGetTeamByUUID } from '@dc-shared-web/hooks/api/team'

import { useAuth } from '@dc-shared-web/providers/AuthProvider'

import useCreateQueryString from '@/app/core/hooks/createQueryString'
import {
  ALL_TEAMS_ID,
  ALL_TEAMS_OPTION,
  SELECTED_TEAM_ID_KEY,
} from '@dc-shared-web/lib/constants'
import { TeamPicker } from '@/app/core/components/TeamPicker'
import { HighSecurityTeamWarningDialog } from '@/app/core/components/HighSecurityTeamWarningDialog'

const getSelectedTeamId = (
  searchParams: ReadonlyURLSearchParams,
  user: User,
  showAllTeamsOption: boolean
) => {
  const selectedTeamId = searchParams.get(SELECTED_TEAM_ID_KEY)

  if (selectedTeamId) {
    return selectedTeamId
  }

  if (user.is_super_admin && showAllTeamsOption) {
    return ALL_TEAMS_ID
  }

  const team = user.teams.find((t) => t.id === user._currOfficeUUID)
  return team?.id
}

interface TeamSelectProps extends React.HTMLAttributes<HTMLButtonElement> {
  disabled?: boolean
  showAdminTeamsOnly?: boolean
  showAllTeamsOption?: boolean
}

export const TeamSelect: FC<TeamSelectProps> = ({
  disabled,
  showAdminTeamsOnly = false,
  showAllTeamsOption = true,
}) => {
  const router = useRouter()
  const pathname = usePathname()
  const { user } = useAuth()
  const { searchParams, createQueryString } = useCreateQueryString()

  const [selectedTeamId, setSelectedTeamId] = useState(
    getSelectedTeamId(searchParams, user, showAllTeamsOption)
  )

  const { mutate: setUserCurrentOfficeMutation } = useSetUserCurrentOffice()

  const teamUUID =
    user.is_super_admin && selectedTeamId && selectedTeamId !== ALL_TEAMS_ID
      ? selectedTeamId
      : undefined

  const { data: currentTeamData, isLoading: isLoadingCurrentTeam } =
    useGetTeamByUUID({
      teamUUID,
    })
  const selectedTeamData = useMemo(() => {
    if (selectedTeamId === ALL_TEAMS_ID) {
      return ALL_TEAMS_OPTION
    }

    if (user.is_super_admin) {
      if (!currentTeamData) return undefined

      return {
        uuid: currentTeamData.uuid,
        id: currentTeamData.id,
        text: currentTeamData.name,
        customer_id: currentTeamData.customerId,
      }
    }

    const team = user.teams.find((t) => t.id === selectedTeamId)

    if (!team) return undefined

    return {
      uuid: team.id,
      id: team.teamId,
      text: team.name,
      customer_id: team.customer_id,
    }
  }, [currentTeamData, selectedTeamId, user])
  const updateTeamUrlParams = useCallback(
    (team: BasicTeam, path: string) => {
      const queryString =
        team.uuid === ALL_TEAMS_ID
          ? createQueryString({
              selectedTeamId: undefined,
              selectedTeam: undefined,
              selectedCustomerId: undefined,
            })
          : createQueryString({
              selectedTeamId: team.uuid,
              selectedTeam: team.text,
              selectedCustomerId: team.customer_id,
            })

      router.replace(`${path}?${queryString}`)
    },
    [createQueryString, router]
  )

  useEffect(() => {
    const urlTeamId = searchParams.get(SELECTED_TEAM_ID_KEY)

    /* URL param changed, update state */
    if (urlTeamId && urlTeamId !== selectedTeamId) {
      setSelectedTeamId(urlTeamId)
    } else if (selectedTeamId && !urlTeamId && selectedTeamData) {
      /* State exists but URL param is missing, sync to URL */
      updateTeamUrlParams(selectedTeamData, pathname)
    }
  }, [
    selectedTeamId,
    searchParams,
    selectedTeamData,
    updateTeamUrlParams,
    pathname,
  ])

  const handleTeamChange = useCallback(
    (team: BasicTeam) => {
      if (
        !user.is_super_admin &&
        team.uuid !== ALL_TEAMS_ID &&
        team.uuid !== user._currOfficeUUID
      ) {
        setUserCurrentOfficeMutation({
          teamId: team.id,
        })
      }

      setSelectedTeamId(team.uuid)

      let path = pathname

      const firstSegment = pathname.split('/')[1]
      const shouldRedirectToDashboard = ['folders', 'projects'].includes(
        firstSegment
      )

      /* When switching teams inside of folders and projects pages, we redirect to the dashboard. */
      if (shouldRedirectToDashboard) {
        path = '/dash'
      }

      updateTeamUrlParams(team, path)
    },
    [user, pathname, setUserCurrentOfficeMutation, updateTeamUrlParams]
  )

  return (
    <>
      <HighSecurityTeamWarningDialog team={currentTeamData} user={user} />
      <TeamPicker
        value={selectedTeamData}
        onChange={handleTeamChange}
        disabled={disabled || isLoadingCurrentTeam}
        showAdminTeamsOnly={showAdminTeamsOnly}
        showAllTeamsOption={showAllTeamsOption}
      />
    </>
  )
}
